import React, { useMemo, useState } from 'react';
import { Button, Drawer, Select, Table, Tooltip, message } from 'antd';
import { BarcodeOutlined, InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as FileSaver from 'file-saver';
import XLSX from 'sheetjs-style';
import { fetchStudioOpsDetail, useStudioOpsBoard, useStudioOpsDetail } from '../../hooks/useStudioOps';
import { useGetCompanyListMinimal } from '../../hooks/useCompanies';
import './index.css';

const COMPANY_KEY = 'studioOpsBoard.companyCodes';

const GROUP_TONE = {
    Receive: 'ops-g-receive',
    PII: 'ops-g-pii',
    Assignment: 'ops-g-assignment',
    Shoot: 'ops-g-shoot',
    Packing: 'ops-g-packing',
    Blocked: 'ops-g-blocked',
};

const COLUMN_HELP = {
    expectedOpen: 'Expected shipments still open: draft, announced, or partially received. Overdue shipments are included here.',
    expectedOverdue: 'Announced or partially received shipments whose ETA has passed. These are also counted in Expected.',
    receivedPendingApproval: 'Received shipments waiting for approval.',
    receivedRejected: 'Shipments that were rejected.',
    approvedPendingCompletion: 'Approved shipments whose inbound has not started or is still in progress.',
    shipmentCompleted: 'Inbound is complete. Closed shipments are not included.',
    piiPending: 'Unique CSV barcodes that are not consumed and can still be used for mapping.',
    piiUnitsReady: 'Shipment units ready for PII.',
    piiUnitsInProgress: 'Shipment units currently in PII.',
    assignmentPending: 'SKUs that are not DD approved and not marked as support. Reorder or callback requests, including completed ones, are excluded.',
    shootPending: 'DD approved, not marked as support, raw QC not complete, and no shoot completion time. Reorder or callback requests with no shoot file are excluded, including completed ones.',
    shootNotStarted: 'Pending shoot with no angle file and no generated angle. Reorder or callback requests are excluded, including completed ones. Not started and Partial add up to Pending.',
    shootPartial: 'Pending shoot with at least one angle file or generated angle. Not started and Partial add up to Pending.',
    rawQcPending: 'SKUs flagged as pending at raw QC. This queue is separate from Pending shoot.',
    packingPendingSkus: 'SKUs ready to pack: raw QC complete or marked as support, not damaged, not outbound, and not yet in a box.',
    openBoxes: 'Boxes that are still open and not outbounded.',
    closedNotAudited: 'Closed boxes that are not audited yet, including audits already in progress.',
    auditedNotOutbounded: 'Audited boxes that have not been outbounded.',
    auditPendingApproval: 'Boxes whose audit is waiting for approval.',
    auditFailed: 'Boxes whose audit failed and were reopened.',
    onHold: 'SKUs on hold that are not outbound and not marked as support.',
    damaged: 'Damaged SKUs that are not outbound and not marked as support.',
};

const COLUMN_MARK = {
    assignmentPending: 'sku',
    shootPending: 'sku',
    shootNotStarted: 'sku',
    shootPartial: 'sku',
    rawQcPending: 'sku',
    packingPendingSkus: 'sku',
    onHold: 'sku',
    damaged: 'sku',
    openBoxes: 'box',
    closedNotAudited: 'box',
    auditedNotOutbounded: 'box',
    auditPendingApproval: 'box',
    auditFailed: 'box',
};

function ColumnTitle({ label, mark, help }) {
    const Icon = mark === 'box' ? InboxOutlined : mark === 'sku' ? BarcodeOutlined : null;
    return (
        <Tooltip title={help}>
            <span className="ops-col-title">
                {Icon ? <Icon /> : null}
                <span>{label}</span>
            </span>
        </Tooltip>
    );
}

function toneCells(className) {
    return {
        className,
        onHeaderCell: () => ({ className }),
        onCell: () => ({ className }),
    };
}

function loadCompanyCodes() {
    try {
        const value = JSON.parse(localStorage.getItem(COMPANY_KEY) || '[]');
        return Array.isArray(value) ? value.map(String) : [];
    } catch (error) {
        return [];
    }
}

function cacheLabel(board, fromClientCache) {
    if (!board) return '';
    const source = board.fromCache || fromClientCache ? 'From cache. ' : '';
    return `${source}Refreshes only when you click Reload.`;
}

function formatWhen(value) {
    return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '';
}

function CountButton({ cell, onClick }) {
    const delta = cell?.delta;
    const changed = delta != null && delta !== 0;
    return (
        <button type="button" className="ops-count" onClick={onClick}>
            <span>{cell?.current || 0}</span>
            {changed ? <small className={delta > 0 ? 'up' : 'down'}>{delta > 0 ? `+${delta}` : delta}</small> : null}
        </button>
    );
}

function sumCells(rows, metrics) {
    const totals = {};
    metrics.forEach(metric => {
        totals[metric.key] = { current: 0, previous: 0, delta: null };
    });
    let hasPrevious = false;
    rows.forEach(row => {
        metrics.forEach(metric => {
            const cell = row.cells[metric.key] || {};
            totals[metric.key].current += cell.current || 0;
            if (cell.previous != null) {
                hasPrevious = true;
                totals[metric.key].previous += cell.previous;
            }
        });
    });
    metrics.forEach(metric => {
        totals[metric.key].previous = hasPrevious ? totals[metric.key].previous : null;
        totals[metric.key].delta = hasPrevious ? totals[metric.key].current - totals[metric.key].previous : null;
    });
    return totals;
}

function excelRows(metrics, rows, totals, field, totalLabel = 'All clients') {
    const body = rows.map(row => {
        const record = { Client: row.companyCode };
        metrics.forEach(metric => {
            record[`${metric.process} — ${metric.label}`] = row.cells[metric.key][field];
        });
        return record;
    });
    const total = { Client: totalLabel };
    metrics.forEach(metric => {
        total[`${metric.process} — ${metric.label}`] = totals[metric.key][field];
    });
    return [...body, total];
}

function detailRecords(detail) {
    return (detail?.rows || []).map(row => {
        const record = {};
        (detail.columns || []).forEach(column => {
            const value = row[column.key];
            record[column.title] = column.key.endsWith('At') || column.key === 'etaDate' ? formatWhen(value) : value ?? '';
        });
        return record;
    });
}

function sheetName(label, used) {
    const clean = String(label || 'Sheet').replace(/[\\/?*[\]:]/g, ' ').trim() || 'Sheet';
    let name = clean.slice(0, 31);
    let n = 2;
    while (used.has(name)) {
        const suffix = ` ${n}`;
        name = clean.slice(0, 31 - suffix.length) + suffix;
        n += 1;
    }
    used.add(name);
    return name;
}

function saveWorkbook(sheets, fileName) {
    const names = Object.keys(sheets);
    const buffer = XLSX.write({ Sheets: sheets, SheetNames: names }, { bookType: 'xlsx', type: 'array' });
    FileSaver.saveAs(
        new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        fileName
    );
}

async function loadDetailExport(params) {
    const res = await fetchStudioOpsDetail({ ...params, pageNum: 1, pageSize: 10000, export: 1 });
    return res?.data?.data;
}

function downloadBoard(data, totalLabel) {
    const current = XLSX.utils.json_to_sheet(excelRows(data.metrics, data.rows, data.totals, 'current', totalLabel));
    const sheets = { Current: current };
    const names = ['Current'];
    if (data.snapshot) {
        sheets.Change = XLSX.utils.json_to_sheet(excelRows(data.metrics, data.rows, data.totals, 'delta', totalLabel));
        sheets.Snapshot = XLSX.utils.json_to_sheet(excelRows(data.metrics, data.rows, data.totals, 'previous', totalLabel));
        names.push('Change', 'Snapshot');
    }
    const buffer = XLSX.write({ Sheets: sheets, SheetNames: names }, { bookType: 'xlsx', type: 'array' });
    FileSaver.saveAs(
        new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `studio-operations-${dayjs().format('YYYY-MM-DD')}.xlsx`
    );
}

export default function StudioOpsBoard() {
    const { data, isFetching, isFetchedAfterMount, isError, error, reload } = useStudioOpsBoard();
    const board = data?.data?.data;
    const { data: companies, isFetching: companiesLoading } = useGetCompanyListMinimal();
    const [companyCodes, setCompanyCodes] = useState(loadCompanyCodes);
    const [drill, setDrill] = useState(null);
    const [pageNum, setPageNum] = useState(1);
    const [exporting, setExporting] = useState('');
    const fromClientCache = !!board && !isFetching && !isFetchedAfterMount;

    const chooseCompanies = codes => {
        setCompanyCodes(codes);
        localStorage.setItem(COMPANY_KEY, JSON.stringify(codes));
    };

    const detailParams = drill
        ? {
            metric: drill.metric,
            companyCode: drill.companyCode,
            pageNum,
            pageSize: 50,
        }
        : null;
    const { data: detailRes, isFetching: detailLoading } = useStudioOpsDetail(detailParams);
    const detail = detailRes?.data?.data;

    const exportOneDetail = async () => {
        if (!drill) return;
        setExporting('one');
        try {
            const detailExport = await loadDetailExport({
                metric: drill.metric,
                companyCode: drill.companyCode,
            });
            const records = detailRecords(detailExport);
            const sheet = XLSX.utils.json_to_sheet(records.length ? records : [{ Note: 'No rows' }]);
            const fileBit = `${drill.title}${drill.companyCode ? `-${drill.companyCode}` : ''}`
                .replace(/[^\w.-]+/g, '-')
                .slice(0, 80);
            saveWorkbook({ Details: sheet }, `${fileBit}-${dayjs().format('YYYY-MM-DD')}.xlsx`);
            if ((detailExport?.total || 0) > records.length) {
                message.warning(`Exported the first ${records.length} of ${detailExport.total} rows.`);
            }
        } catch (err) {
            message.error(err?.response?.data?.message || err?.message || 'Export failed');
        } finally {
            setExporting('');
        }
    };

    const exportAllDetails = async () => {
        if (!board?.metrics?.length) return;
        setExporting('all');
        const targets = companyCodes.length ? companyCodes : [undefined];
        const sheets = {};
        const used = new Set();
        let truncated = false;
        try {
            for (const metric of board.metrics) {
                const records = [];
                for (const companyCode of targets) {
                    const detailExport = await loadDetailExport({ metric: metric.key, companyCode });
                    records.push(...detailRecords(detailExport));
                    if ((detailExport?.total || 0) > (detailExport?.rows || []).length) truncated = true;
                }
                const name = sheetName(`${metric.process} ${metric.label}`, used);
                sheets[name] = XLSX.utils.json_to_sheet(records.length ? records : [{ Note: 'No rows' }]);
            }
            saveWorkbook(sheets, `studio-operations-details-${dayjs().format('YYYY-MM-DD')}.xlsx`);
            if (truncated) message.warning('Some sheets include only the first 10,000 rows for a client.');
        } catch (err) {
            message.error(err?.response?.data?.message || err?.message || 'Export failed');
        } finally {
            setExporting('');
        }
    };

    const view = useMemo(() => {
        if (!board) return null;
        if (!companyCodes.length) return { rows: board.rows, totals: board.totals, totalLabel: 'All clients' };
        const selected = new Set(companyCodes);
        const rows = board.rows.filter(row => selected.has(row.companyCode));
        return { rows, totals: sumCells(rows, board.metrics), totalLabel: 'Selected' };
    }, [board, companyCodes]);

    const companyOptions = useMemo(() => {
        const byCode = new Map();
        (companies || []).forEach(company => {
            if (company?.code) byCode.set(company.code, company.name || '');
        });
        (board?.rows || []).forEach(row => {
            if (row.companyCode && !byCode.has(row.companyCode)) byCode.set(row.companyCode, '');
        });
        return [...byCode.keys()]
            .sort()
            .map(code => ({ value: code, label: byCode.get(code) ? `${code} — ${byCode.get(code)}` : code }));
    }, [companies, board]);

    const columns = useMemo(() => {
        if (!board) return [];
        const groups = [];
        board.metrics.forEach(metric => {
            const tone = GROUP_TONE[metric.process] || 'ops-g-receive';
            let group = groups.find(item => item.title === metric.process);
            if (!group) {
                group = { title: metric.process, children: [], ...toneCells(`${tone} ops-g-start`) };
                groups.push(group);
            }
            const cellTone = group.children.length ? tone : `${tone} ops-g-start`;
            group.children.push({
                title: <ColumnTitle label={metric.label} mark={COLUMN_MARK[metric.key]} help={COLUMN_HELP[metric.key]} />,
                width: 130,
                ...toneCells(cellTone),
                render: (_, row) => (
                    <CountButton
                        cell={row.cells[metric.key]}
                        onClick={() => {
                            const companyCode =
                                row.companyCode === 'All clients'
                                    ? undefined
                                    : row.companyCode === 'Selected'
                                        ? companyCodes.length === 1
                                            ? companyCodes[0]
                                            : null
                                        : row.companyCode;
                            if (companyCode === null) return;
                            setPageNum(1);
                            setDrill({
                                metric: metric.key,
                                companyCode,
                                title: `${metric.process} · ${metric.label}`,
                            });
                        }}
                    />
                ),
            });
        });
        return [{ title: 'Client', dataIndex: 'companyCode', fixed: 'left', width: 110 }, ...groups];
    }, [board, companyCodes]);

    const tableRows = view
        ? [
            ...view.rows,
            {
                companyCode: view.totalLabel,
                cells: view.totals,
            },
        ]
        : [];

    const snapshotText = board?.snapshot
        ? `Compared with the ${dayjs(board.snapshot.takenAt).format('D MMM YYYY HH:mm')} snapshot (06:30 Europe/Berlin, ${board.snapshot.slot}). A higher number is more work still in that queue.`
        : 'No 06:30 Europe/Berlin snapshot yet. The first one is stored the next time reports run after 06:30.';
    const overlapText =
        'Expected overdue is inside Expected. Not started and Partial add up to Pending shoot. Closed, not audited includes audits already in progress.';

    return (
        <div className="ops-board">
            <div className="ops-board-head">
                <div>
                    <h2>Studio operations</h2>
                    <p>
                        Current queues for every client
                        {board?.generatedAt ? `, as of ${formatWhen(board.generatedAt)}` : ''}. Counts are limited to the last 12 months.{' '}
                        {cacheLabel(board, fromClientCache)} {snapshotText} {overlapText}
                    </p>
                </div>
                <div className="ops-board-actions">
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        placeholder="All clients"
                        value={companyCodes}
                        options={companyOptions}
                        loading={companiesLoading}
                        optionFilterProp="label"
                        maxTagCount={2}
                        style={{ minWidth: 280 }}
                        onChange={chooseCompanies}
                    />
                    <Button onClick={reload} loading={isFetching && !!board}>
                        Reload
                    </Button>
                    <Button onClick={exportAllDetails} disabled={!board} loading={exporting === 'all'}>
                        Export all details
                    </Button>
                    <Button
                        type="primary"
                        disabled={!view}
                        onClick={() => downloadBoard({ ...board, rows: view.rows, totals: view.totals }, view.totalLabel)}
                    >
                        Export to Excel
                    </Button>
                </div>
            </div>
            {isError ? <p>{error?.response?.data?.message || error?.message || 'Could not load the board'}</p> : null}
            <Table
                loading={isFetching}
                rowKey="companyCode"
                size="small"
                pagination={false}
                scroll={{ x: 'max-content' }}
                columns={columns}
                dataSource={tableRows}
                rowClassName={row => (row.companyCode === 'All clients' || row.companyCode === 'Selected' ? 'ops-total' : '')}
            />
            <Drawer
                title={drill ? `${drill.title}${drill.companyCode ? ` · ${drill.companyCode}` : ''}` : ''}
                width={860}
                open={!!drill}
                onClose={() => setDrill(null)}
                extra={
                    <Button onClick={exportOneDetail} loading={exporting === 'one'}>
                        Export this list
                    </Button>
                }
            >
                <Table
                    loading={detailLoading}
                    rowKey={(row, index) => `${row.barcode || row.skuName || row.boxId || row.code || index}`}
                    size="small"
                    dataSource={detail?.rows || []}
                    columns={(detail?.columns || []).map(column => ({
                        title: column.title,
                        dataIndex: column.key,
                        render: value => (column.key.endsWith('At') || column.key === 'etaDate' ? formatWhen(value) : value),
                    }))}
                    pagination={{
                        current: pageNum,
                        pageSize: 50,
                        total: detail?.total || 0,
                        showSizeChanger: false,
                        onChange: page => setPageNum(page),
                    }}
                />
            </Drawer>
        </div>
    );
}
